// What Tiendas Biai can do, as data — `NAV-1` (`TBS-77`).
//
// One list, read by three things: the header on every management screen, the cards on `/gestion`,
// and a test that checks each entry against the route table. Two lists would drift, and the way
// they drift is silent — a screen nobody can reach, or a menu entry that 404s.
//
// **The wording lives here rather than in `constants.ts`**, against the convention that file states,
// because a label and the path it opens are one fact. "Traslados" pointing at the ledger is not a
// wording mistake, and splitting the two across files is what would let it happen.
//
// **`requiredRole` mirrors the route's guard, which mirrors the endpoint's `x-role`.** It is not a
// second opinion about who may do what: the server decides, the guard refuses, and this only
// decides whether the door is drawn. `navigation.test.ts` and `tbsRoutes.test.tsx` hold the three
// together.

export type TbsNavRole = "viewer" | "cashier" | "admin";

/** The icons the header and the landing draw. Mapped to components in `navIcons.tsx`. */
export type TbsNavIcon =
  | "products"
  | "inventory"
  | "pos"
  | "invoices"
  | "payments"
  | "contacts"
  | "reports"
  | "config";

export interface TbsNavItem {
  label: string;
  path: string;
  /** The least privileged role that may open it. Equal to the route's guard, never wider. */
  requiredRole: TbsNavRole;
  /** One line saying what the screen is for. The landing shows it; the header has no room. */
  description: string;
}

export interface TbsNavSection {
  label: string;
  icon: TbsNavIcon;
  /** What the area is for, in the words the shop uses. Shown on the landing card. */
  description: string;
  items: TbsNavItem[];
}

/**
 * The eight areas, in the order the header shows them.
 *
 * Ordered by how often somebody opens them rather than alphabetically or by the plan's section
 * numbering: the till and what it sells come first, configuration last because it is opened once.
 */
export const TBS_NAV: TbsNavSection[] = [
  {
    label: "Punto de venta",
    icon: "pos",
    description: "Vender en el mostrador.",
    items: [
      {
        label: "Vender",
        path: "/pos",
        requiredRole: "cashier",
        description: "La pantalla del mostrador: escanear, cobrar y entregar.",
      },
      {
        label: "Turno de caja",
        path: "/pos/turno",
        requiredRole: "cashier",
        description: "Abrir la caja con su base, y cuadrarla al cerrar.",
      },
    ],
  },
  {
    label: "Productos",
    icon: "products",
    description: "El catálogo que lee la caja.",
    items: [
      {
        label: "Productos",
        path: "/gestion/productos",
        requiredRole: "viewer",
        description: "Precios, referencias, impuestos y fotos.",
      },
    ],
  },
  {
    label: "Inventario",
    icon: "inventory",
    description: "Qué hay, dónde está y por qué cambió.",
    items: [
      {
        label: "Movimientos",
        path: "/gestion/inventario/movimientos",
        requiredRole: "viewer",
        description: "Todo lo que ha entrado y salido, con su motivo.",
      },
      {
        label: "Entrada",
        path: "/gestion/inventario/entrada",
        requiredRole: "admin",
        description: "Registrar existencias que entran, o corregir las que están mal.",
      },
      {
        label: "Traslados",
        path: "/gestion/inventario/traslados",
        requiredRole: "admin",
        description: "Mover existencias de una bodega a otra.",
      },
    ],
  },
  {
    label: "Facturas",
    icon: "invoices",
    description: "Lo que se ha vendido.",
    items: [
      {
        label: "Facturas",
        path: "/gestion/facturas",
        requiredRole: "viewer",
        description: "Las ventas emitidas, con su saldo y sus pagos.",
      },
      {
        label: "Pendientes DIAN",
        path: "/gestion/facturas/pendientes-dian",
        requiredRole: "viewer",
        description: "Ventas que todavía no tienen su factura electrónica.",
      },
    ],
  },
  {
    label: "Pagos",
    icon: "payments",
    description: "El dinero que entró.",
    items: [
      {
        label: "Pagos",
        path: "/gestion/pagos",
        requiredRole: "viewer",
        description: "Cada pago del día, con el medio por el que entró.",
      },
    ],
  },
  {
    label: "Contactos",
    icon: "contacts",
    description: "Con quién se trabaja.",
    items: [
      {
        label: "Contactos",
        path: "/gestion/contactos",
        requiredRole: "viewer",
        description: "Clientes y proveedores.",
      },
    ],
  },
  {
    label: "Reportes",
    icon: "reports",
    description: "Cómo va el día.",
    items: [
      {
        label: "Resumen diario",
        path: "/gestion/reportes/diario",
        requiredRole: "viewer",
        description: "El movimiento del día en efectivo y en banco.",
      },
      {
        label: "Contabilidad",
        path: "/gestion/contabilidad",
        requiredRole: "admin",
        description: "La base del día, y el saldo con el que se cierra.",
      },
    ],
  },
  {
    label: "Configuración",
    icon: "config",
    description: "Lo que una venta necesita para poder emitirse.",
    items: [
      {
        label: "Configuración",
        path: "/gestion/configuracion",
        requiredRole: "admin",
        description: "Bodegas, impuestos, cajas y numeraciones.",
      },
    ],
  },
];

/** Whether the caller holds a role. Passed in so this module needs neither Auth0 nor Redux. */
export type RoleCheck = (role: TbsNavRole) => boolean;

/**
 * The navigation as this caller should see it.
 *
 * **A section with nothing left in it disappears**, rather than staying as a heading over an empty
 * menu: a cashier has no business seeing that "Contabilidad" exists and is closed to them. The one
 * case worth naming is Inventario, which keeps its Movimientos entry for a viewer and loses the two
 * admin ones — the section survives with a shorter menu, which is right.
 */
export const visibleNavigation = (
  can: RoleCheck,
  sections: TbsNavSection[] = TBS_NAV,
): TbsNavSection[] =>
  sections
    .map((section) => ({ ...section, items: section.items.filter((i) => can(i.requiredRole)) }))
    .filter((section) => section.items.length > 0);

/** Every path in the navigation, flattened. Used by the test that checks them against the routes. */
export const navPaths = (sections: TbsNavSection[] = TBS_NAV): string[] =>
  sections.flatMap((section) => section.items.map((item) => item.path));
